import React, { useState } from 'react';
import { Arbeitsgruppe, File, MediaFile } from '../types';
import Alert from './ErrorDisplay';
import MediaViewer from './MediaViewer';
import DownloadProgress from './DownloadProgress';
import { Device } from '@capacitor/device';
import {downloadAndSaveToDocumentsForMobile, openDocument, webDownload} from '../libraries/media';

interface ChallengeResultsPageProps {
    arbeitsgruppen: Arbeitsgruppe[];
}

const ChallengeResultsPage: React.FC<ChallengeResultsPageProps> = ({ arbeitsgruppen }) => {
    const [selectedArbeitsgruppe, setSelectedArbeitsgruppe] = useState<Arbeitsgruppe | null>(null);
    const [selectedMediaIndex, setSelectedMediaIndex] = useState<number | null>(null);
    const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(null);
    const [devicePlatform, setDevicePlatform] = useState<string | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadProgress, setDownloadProgress] = useState(0);
    const [downloadBytes, setDownloadBytes] = useState(0);
    const [downloadTotal, setDownloadTotal] = useState(0);
    const [downloadError, setDownloadError] = useState<string | undefined>();
    const [downloadComplete, setDownloadComplete] = useState(false);

    React.useEffect(() => {
        const detectPlatform = async () => {
            try {
                const info = await Device.getInfo();
                setDevicePlatform(info.platform);
            } catch (error) {
                console.error('Failed to detect device platform:', error);
            }
        };
        void detectPlatform();
    }, []);

    // Filter arbeitsgruppen that have result files
    const arbeitsgruppenmitResultate = arbeitsgruppen.filter(ag => ag.resultFiles && ag.resultFiles.length > 0);

    const handleArbeitsgruppeClick = (ag: Arbeitsgruppe) => {
        setSelectedArbeitsgruppe(ag);
        setSelectedMediaIndex(null);
    };

    const handleBackToList = () => {
        setSelectedArbeitsgruppe(null);
        setSelectedMediaIndex(null);
    };

    // Check if file is media (image, video, or audio including mp3)
    const isMediaFile = (file: File): boolean => {
        return file.mimetype.startsWith('image/') ||
               file.mimetype.startsWith('video/') ||
               file.mimetype.startsWith('audio/');
    };

    // Convert media files to MediaFile format
    const convertToMediaFiles = (files: File[]): MediaFile[] => {
        return files.filter(isMediaFile).map(file => ({
            id: file.id,
            filename: file.filename,
            caption: file.caption || null,
            url: file.url,
            mimetype: file.mimetype,
            thumbnailUrl: file.thumbnailUrl,
            width: file.width || 0,
            height: file.height || 0,
        }));
    };

    // Handle document download
    const handleDownloadDocument = async (file: File) => {
        setSelectedDocumentId(file.id);
        setIsDownloading(true);
        setDownloadError(undefined);
        setDownloadComplete(false);
        setDownloadProgress(0);
        setDownloadBytes(0);
        setDownloadTotal(0);

        try {
            const onProgress = (bytes: number, contentLength: number) => {
                setDownloadBytes(bytes);
                setDownloadTotal(contentLength);
                if (contentLength > 0) {
                    setDownloadProgress(Math.round((bytes / contentLength) * 100));
                }
            };

            if (devicePlatform === 'ios' || devicePlatform === 'android') {
                await downloadAndSaveToDocumentsForMobile(file.url, file.filename, onProgress);
            } else {
                // Web download
                await webDownload(file.url, file.filename);
            }

            setDownloadComplete(true);
            setTimeout(() => {
                setIsDownloading(false);
                setDownloadComplete(false);
                setSelectedDocumentId(null);
            }, 2000);
        } catch (error) {
            console.error('Download error:', error);
            setDownloadError(error instanceof Error ? error.message : 'Download fehlgeschlagen');
            setTimeout(() => {
                setIsDownloading(false);
                setSelectedDocumentId(null);
            }, 3000);
        }
    };

    // Handle opening document in new tab
    const handleOpenDocument = async (file: File) => {
        openDocument(file.url)
    };

    if (arbeitsgruppenmitResultate.length === 0) {
        return (
            <Alert
                title="Keine Ergebnisse"
                message="Es wurden noch keine Challenge-Ergebnisse verfügbar gemacht."
                type="info"
            />
        );
    }

    // List View
    if (!selectedArbeitsgruppe) {
        return (
            <div className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {arbeitsgruppenmitResultate.map((ag) => (
                        <div
                            key={ag.id}
                            onClick={() => handleArbeitsgruppeClick(ag)}
                            className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-all cursor-pointer overflow-hidden flex flex-col h-full"
                        >
                            <div className="p-6 grow">
                                <div className="flex justify-between items-start mb-4">
                                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-sm">
                                        {ag.challenge}
                                    </span>
                                    <span className="text-xs font-medium px-2.5 py-0.5 rounded bg-gray-100 text-gray-800">
                                        {ag.resultFiles.length} Datei{ag.resultFiles.length !== 1 ? 'en' : ''}
                                    </span>
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">{ag.name}</h3>

                                <div className="space-y-2 mt-4">
                                    {ag.coach && (
                                        <div className="flex items-center text-sm text-gray-500">
                                            <span className="font-medium mr-2">Coach:</span>
                                            <span>{ag.coach}</span>
                                        </div>
                                    )}
                                    {ag.room && (
                                        <div className="flex items-center text-sm text-gray-500">
                                            <span className="font-medium mr-2">Raum:</span>
                                            <span>{ag.room}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                                <span className="text-primary text-sm font-medium hover:text-primary/80 transition-colors flex items-center">
                                    Ergebnisse ansehen
                                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                    </svg>
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Detail View - File Viewer
    if (!selectedArbeitsgruppe.resultFiles || selectedArbeitsgruppe.resultFiles.length === 0) {
        return (
            <div className="space-y-4">
                <button
                    onClick={handleBackToList}
                    className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors mb-4"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Zurück
                </button>
                <h2 className="text-2xl font-bold text-gray-900">{selectedArbeitsgruppe.name}</h2>
                <Alert
                    title="Keine Dateien"
                    message="Diese Arbeitsgruppe hat noch keine Ergebnisdateien."
                    type="info"
                />
            </div>
        );
    }
    const allFiles = selectedArbeitsgruppe.resultFiles;
    const mediaFiles = convertToMediaFiles(allFiles);

    // Separate files by type
    const images = allFiles.filter(f => f.mimetype.startsWith('image/'));
    const videos = allFiles.filter(f => f.mimetype.startsWith('video/'));
    const audios = allFiles.filter(f => f.mimetype.startsWith('audio/'));
    const documents = allFiles.filter(f => !isMediaFile(f));

    return (
        <div className="space-y-6">
            <div>
                <button
                    onClick={handleBackToList}
                    className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors mb-4"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Zurück
                </button>
                <h2 className="text-2xl font-bold text-gray-900">{selectedArbeitsgruppe.name}</h2>
            </div>

            {/* Images Section */}
            {images.length > 0 && (
                <section>
                    <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <span className="material-symbols-outlined text-pink-600" style={{ fontSize: '1.25rem' }}>
                            photo_library
                        </span>
                        Bilder
                        <span className="text-sm font-normal text-gray-400">({images.length})</span>
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                        {images.map((image) => {
                            const mediaIndex = mediaFiles.findIndex(m => m.id === image.id);
                            const aspectRatio = image.width && image.height ? image.height / image.width : 1;
                            return (
                                <button
                                    key={image.id}
                                    onClick={() => setSelectedMediaIndex(mediaIndex)}
                                    className="relative w-full overflow-hidden rounded-lg bg-gray-100 hover:opacity-90 active:scale-95 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-pink-500 shadow-sm"
                                    aria-label={image.caption || image.filename}
                                    style={{ aspectRatio: `1 / ${aspectRatio}` }}
                                >
                                    <img
                                        src={image.thumbnailUrl || image.url}
                                        alt={image.caption || image.filename}
                                        className="w-full h-full object-cover"
                                        loading="lazy"
                                    />
                                    {image.caption && (
                                        <div className="absolute bottom-0 left-0 right-0 bg-linear-to-t from-black/60 to-transparent px-2 py-1">
                                            <p className="text-white text-xs truncate">{image.caption}</p>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Videos Section */}
            {videos.length > 0 && (
                <section>
                    <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <span className="material-symbols-outlined text-pink-600" style={{ fontSize: '1.25rem' }}>
                            video_library
                        </span>
                        Videos
                        <span className="text-sm font-normal text-gray-400">({videos.length})</span>
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                        {videos.map((video) => {
                            const mediaIndex = mediaFiles.findIndex(m => m.id === video.id);
                            const aspectRatio = video.width && video.height ? video.height / video.width : 9 / 16;
                            return (
                                <button
                                    key={video.id}
                                    onClick={() => setSelectedMediaIndex(mediaIndex)}
                                    className="relative w-full overflow-hidden rounded-lg bg-gray-900 hover:opacity-90 active:scale-95 transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-pink-500 shadow-sm"
                                    aria-label={video.caption || video.filename}
                                    style={{ aspectRatio: `1 / ${aspectRatio}` }}
                                >
                                    {video.thumbnailUrl ? (
                                        <img
                                            src={video.thumbnailUrl}
                                            alt={video.caption || video.filename}
                                            className="w-full h-full object-cover"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center bg-gray-800">
                                            <span className="material-symbols-outlined text-white/40" style={{ fontSize: '3rem' }}>
                                                movie
                                            </span>
                                        </div>
                                    )}
                                    {/* Play overlay */}
                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="w-14 h-14 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-white/20">
                                            <span className="material-symbols-outlined text-white" style={{ fontSize: '2rem' }}>
                                                play_arrow
                                            </span>
                                        </div>
                                    </div>
                                    {video.caption && (
                                        <div className="px-3 py-2 bg-gray-900 text-left">
                                            <p className="text-white/80 text-sm truncate">{video.caption}</p>
                                        </div>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Audio Section */}
            {audios.length > 0 && (
                <section>
                    <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <span className="material-symbols-outlined text-purple-600" style={{ fontSize: '1.25rem' }}>
                            audio_file
                        </span>
                        Audio
                        <span className="text-sm font-normal text-gray-400">({audios.length})</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {audios.map((audio) => {
                            const mediaIndex = mediaFiles.findIndex(m => m.id === audio.id);
                            return (
                                <button
                                    key={audio.id}
                                    onClick={() => setSelectedMediaIndex(mediaIndex)}
                                    className="flex items-center gap-4 p-4 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:shadow-sm transition-all cursor-pointer group"
                                >
                                    <div className="shrink-0 w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-purple-600">
                                            audiotrack
                                        </span>
                                    </div>
                                    <div className="grow min-w-0 text-left">
                                        <p className="font-medium text-gray-900 truncate group-hover:text-primary transition-colors">
                                            {audio.caption || audio.filename}
                                        </p>
                                    </div>
                                    <span className="shrink-0 text-gray-400 group-hover:text-primary transition-colors">
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                        </svg>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Documents Section */}
            {documents.length > 0 && (
                <section>
                    <h3 className="text-lg font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <span className="material-symbols-outlined text-blue-600" style={{ fontSize: '1.25rem' }}>
                            description
                        </span>
                        Dateien
                        <span className="text-sm font-normal text-gray-400">({documents.length})</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {documents.map((doc) => {
                            return (
                                <div
                                    key={doc.id}
                                    className="flex flex-col gap-3 p-4 rounded-lg border border-gray-200 bg-white hover:shadow-sm transition-all"
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="shrink-0 w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden">
                                            {doc.iconUrl ? (
                                                <img
                                                    src={doc.iconUrl}
                                                    alt={doc.filename}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <span className="material-symbols-outlined text-blue-600">
                                                    description
                                                </span>
                                            )}
                                        </div>
                                        <div className="grow min-w-0 text-left">
                                            <p className="font-medium text-gray-900 truncate">
                                                {doc.caption || doc.filename}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => handleOpenDocument(doc)}
                                            className="flex-1 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors font-medium text-sm inline-flex items-center justify-center gap-2"
                                        >
                                            <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>
                                                open_in_new
                                            </span>
                                            Öffnen
                                        </button>
                                        <button
                                            onClick={() => handleDownloadDocument(doc)}
                                            disabled={isDownloading && selectedDocumentId === doc.id}
                                            className="flex-1 px-3 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-sm inline-flex items-center justify-center gap-2"
                                        >
                                            <span className="material-symbols-outlined" style={{ fontSize: '1rem' }}>
                                                download
                                            </span>
                                            {isDownloading && selectedDocumentId === doc.id ? 'Lädt...' : 'Herunterladen'}
                                        </button>
                                    </div>
                                    {/* Download Progress - Only on mobile */}
                                    {isDownloading && selectedDocumentId === doc.id && downloadTotal > 0 && (devicePlatform === 'ios' || devicePlatform === 'android') && (
                                        <div className="w-full">
                                            <DownloadProgress
                                                fileName={doc.filename}
                                                isVisible={true}
                                                progress={downloadProgress}
                                                bytes={downloadBytes}
                                                contentLength={downloadTotal}
                                                isComplete={downloadComplete}
                                                error={downloadError}
                                                onClose={() => {
                                                    setIsDownloading(false);
                                                    setDownloadError(undefined);
                                                    setDownloadComplete(false);
                                                    setSelectedDocumentId(null);
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            {/* Media Viewer for images, videos, and audio */}
            {selectedMediaIndex !== null && selectedMediaIndex >= 0 && selectedMediaIndex < mediaFiles.length && (
                <MediaViewer
                    media={mediaFiles}
                    startIndex={selectedMediaIndex}
                    onClose={() => setSelectedMediaIndex(null)}
                />
            )}
        </div>
    );
};

export default ChallengeResultsPage;








